import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle } from 'lucide-react';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/apiClient';

interface HandoffDetail {
  id: string;
  leadId: string;
  raisedBy: string;
  raisedByEmail: string | null;
  state: string;
  handoffNote: string | null;
  feedback: string | null;
  createdAt: string;
  leadFirstName: string | null;
  leadLastName: string | null;
  leadEmail: string | null;
  leadCompanyName: string | null;
}

interface AcceptResult {
  id: string;
}

function formatAge(ts: string): string {
  const diffMs = Date.now() - new Date(ts).getTime();
  const days = Math.floor(diffMs / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return '1d ago';
  return `${days}d ago`;
}

export function HandoffInboxPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [handoffs, setHandoffs] = useState<HandoffDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const [accepting, setAccepting] = useState<string | null>(null);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get<HandoffDetail[]>('/lead-handoffs');
      setHandoffs(data);
    } catch {
      setHandoffs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleAccept(id: string) {
    setAccepting(id);
    try {
      const { data } = await apiClient.post<AcceptResult>(`/lead-handoffs/${id}/accept`);
      showToast(t('handoff.accept_success'));
      navigate(`/opportunities/${data.id}`);
    } catch {
      showToast(t('handoff.error_submit'));
    } finally {
      setAccepting(null);
    }
  }

  function openReject(id: string) {
    setRejectId(id);
    setFeedback('');
    setRejectOpen(true);
  }

  async function submitReject() {
    if (!rejectId) return;
    setRejecting(true);
    try {
      await apiClient.post(`/lead-handoffs/${rejectId}/reject`, { feedback: feedback.trim() || null });
      showToast(t('handoff.reject_success'));
      setRejectOpen(false);
      load();
    } catch {
      showToast(t('handoff.error_submit'));
    } finally {
      setRejecting(false);
    }
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <Breadcrumbs
        crumbs={[{ label: t('handoff.inbox_title') }]}
      />
      <h1 className="text-2xl font-semibold mt-2">{t('handoff.inbox_title')}</h1>
      <p className="text-muted-foreground text-sm mt-1">{t('handoff.inbox_subtitle')}</p>

      {toast && (
        <div className="fixed bottom-4 right-4 bg-foreground text-background px-4 py-2 rounded shadow z-50 text-sm">
          {toast}
        </div>
      )}

      {loading ? (
        <p className="text-muted-foreground mt-6 text-sm">Loading…</p>
      ) : handoffs.length === 0 ? (
        <p className="text-muted-foreground mt-6 text-sm">{t('handoff.inbox_empty')}</p>
      ) : (
        <div className="mt-6 border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2">{t('handoff.col_lead')}</th>
                <th className="text-left px-4 py-2">{t('handoff.col_company')}</th>
                <th className="text-left px-4 py-2">{t('handoff.col_raised_by')}</th>
                <th className="text-left px-4 py-2">{t('handoff.col_note')}</th>
                <th className="text-left px-4 py-2">{t('handoff.col_age')}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {handoffs.map((h) => (
                <tr key={h.id} className="hover:bg-muted/20">
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate(`/leads/${h.leadId}`)}
                      className="text-primary hover:underline text-left"
                    >
                      {[h.leadFirstName, h.leadLastName].filter(Boolean).join(' ') || h.leadEmail || h.leadId.slice(0, 8)}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{h.leadCompanyName ?? '—'}</td>
                  <td className="px-4 py-3 text-muted-foreground">{h.raisedByEmail ?? h.raisedBy.slice(0, 8)}</td>
                  <td className="px-4 py-3 max-w-xs">
                    <p className="truncate text-muted-foreground">{h.handoffNote ?? '—'}</p>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{formatAge(h.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={accepting === h.id}
                        onClick={() => handleAccept(h.id)}
                        className="gap-1"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        {accepting === h.id ? '…' : t('handoff.accept')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openReject(h.id)}
                      >
                        <XCircle className="h-3.5 w-3.5 mr-1" />
                        {t('handoff.reject')}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reject dialog */}
      {rejectOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-card border rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="font-semibold text-lg mb-4">{t('handoff.reject_dialog_title')}</h2>
            <label className="block text-sm font-medium mb-1">{t('handoff.feedback_label')}</label>
            <textarea
              className="w-full border rounded-md p-2 text-sm resize-none h-24 bg-background"
              placeholder={t('handoff.feedback_placeholder')}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setRejectOpen(false)} disabled={rejecting}>
                {t('common.cancel')}
              </Button>
              <Button onClick={submitReject} disabled={rejecting}>
                {rejecting ? '…' : t('handoff.reject_submit')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
